import React, { useEffect, memo } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  XCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

/**
 * Componente individual de notificação
 * ✅ FASE 1: Otimizado com memoização
 */
const Notification = memo(({ notification, onClose }) => {
  const { isDarkMode } = useTheme();
  
  // Configurar fechamento automático para cards (5 segundos)
  useEffect(() => {
    if (notification.autoClose && notification.duration > 0) {
      const timer = setTimeout(() => {
        onClose(notification.id);
      }, notification.duration);
      
      return () => clearTimeout(timer);
    }
  }, [notification.id, notification.autoClose, notification.duration, onClose]);
  
  // Determinar ícone com base no tipo
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
      case 'error':
        return <XCircleIcon className="h-6 w-6 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
      case 'info':
      default:
        return <InformationCircleIcon className="h-6 w-6 text-blue-500" />;
    }
  };
  
  // Determinar cores com base no tipo e tema
  const getBgColor = () => {
    if (isDarkMode) {
      switch (notification.type) {
        case 'success': return 'bg-green-900 bg-opacity-50';
        case 'error': return 'bg-red-900 bg-opacity-50';
        case 'warning': return 'bg-yellow-900 bg-opacity-50';
        case 'info':
        default: return 'bg-blue-900 bg-opacity-50';
      }
    } else {
      switch (notification.type) {
        case 'success': return 'bg-green-50';
        case 'error': return 'bg-red-50';
        case 'warning': return 'bg-yellow-50';
        case 'info':
        default: return 'bg-blue-50';
      }
    }
  };
  
  return (
    <div className={`max-w-sm w-full ${getBgColor()} shadow-md rounded-lg pointer-events-auto overflow-hidden`}>
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {notification.title}
            </p>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
              {notification.message}
            </p>
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className={`rounded-md inline-flex ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
              onClick={() => onClose(notification.id)}
            >
              <span className="sr-only">Fechar</span>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

// Adicionar displayName para melhor debugging
Notification.displayName = 'Notification';

/**
 * Container para todas as notificações CARD (5 segundos)
 * ✅ FASE 1: Otimizado com memoização
 */
const NotificationContainer = memo(() => {
  const { cardNotifications, removeCardNotification } = useNotification();
  
  if (cardNotifications.length === 0) {
    return null;
  }
  
  return (
    <div className="fixed top-20 right-4 z-[65] flex flex-col gap-3 pointer-events-none max-w-sm w-full">
      {cardNotifications.map((notification) => (
        <Notification
          key={notification.id}
          notification={notification}
          onClose={removeCardNotification}
        />
      ))}
    </div>
  );
});

// Adicionar displayName para melhor debugging
NotificationContainer.displayName = 'NotificationContainer';

export default NotificationContainer; 